import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Check, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [order, setOrder] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    let cancelled = false;
    let attempts = 0;
    const MAX = 8;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrder(data);
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setStatus("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear, navigate]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-20 bg-sand" data-testid="checkout-success-page">
      <div className="max-w-xl w-full bg-white border border-navy/10 p-10 md:p-14 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-seafoam-600 mx-auto animate-spin mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-3xl font-bold text-navy">Confirming your payment...</h1>
            <p className="text-navy/60 mt-3 text-sm">Just a moment — we're locking in your order.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 bg-seafoam-600 text-white rounded-full mx-auto flex items-center justify-center mb-6">
              <Check className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <p className="b2t-overline mb-2">Order Confirmed</p>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-navy" data-testid="success-message">
              Tide's in. Order's locked.
            </h1>
            <p className="text-navy/60 mt-3 text-sm">
              Thanks for shopping with Boat 2 Tee. We've got the order — confirmation is on the way.
            </p>
            {order?.order_id && (
              <p className="mt-4 text-xs uppercase tracking-widest text-navy/40">
                Order Ref: <span className="text-navy font-semibold">{order.order_id.slice(0, 8).toUpperCase()}</span>
              </p>
            )}
            <Link
              to="/shop"
              data-testid="success-continue-shopping"
              className="mt-8 inline-block bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] hover:bg-seafoam hover:text-navy transition-colors"
            >
              Keep Shopping
            </Link>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <h1 className="font-display text-3xl font-bold text-navy">
              {status === "expired" ? "Session expired" : "Couldn't confirm payment"}
            </h1>
            <p className="text-navy/60 mt-3 text-sm">
              Don't worry — no charge was made. Your bag is still saved.
            </p>
            <Link
              to="/cart"
              className="mt-6 inline-block bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] hover:bg-seafoam hover:text-navy transition-colors"
            >
              Back to Cart
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
